import { Alarm, Envelope, Prohibit } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { ButtonGroup, Header, SlideLayout, Text } from "../../shared-components";
import { setError } from "../../utils/setError";
import {
  FlexMeeting,
  FlexMeetingEventMap,
  getEventListFromFlexMeetingMap,
} from "./utils/suggestion-utils";

import { Select, SelectOption, Switch } from "@clockwise/design-system";
import { RecurrenceType } from "@clockwise/schema";
import classNames from "classnames";
import { TrackingEvents, track } from "../../../../util/analytics.util";
import { useOnboardingUpdateEventFlexibility } from "../../hooks/useOnboardingUpdateEventFlexibility";
import { FunnelType } from "../../WebOnboarding.util";

export const FlexMeetingSlide = ({
  eventMap,
  showSecondaryButton,
  onClickPrimaryButton,
  onClickSecondaryButton,
  canSetDisabled,
  funnelType,
}: {
  showSecondaryButton: boolean;
  onClickPrimaryButton: () => void;
  onClickSecondaryButton: () => void;
  eventMap: FlexMeetingEventMap;
  canSetDisabled: boolean;
  funnelType: FunnelType;
}) => {
  const { update } = useOnboardingUpdateEventFlexibility();
  const [flexMeetingEventMap, setFlexMeetingEventMap] = useState<FlexMeetingEventMap>({});
  const [disableButtons, setDisableButtons] = useState(false);
  const flexMeetingList = getEventListFromFlexMeetingMap(flexMeetingEventMap);
  const sortedMeetingsByRecurrence = sortFlexMeetings(flexMeetingList);

  useEffect(() => {
    setFlexMeetingEventMap({ ...eventMap });
  }, [eventMap]);

  const updateFlexibility = (eventId: string, isFlexible: boolean) => {
    const eventToUpdate = { ...flexMeetingEventMap[eventId] };
    eventToUpdate.flexible = isFlexible;
    setFlexMeetingEventMap({ ...flexMeetingEventMap, [eventId]: eventToUpdate });
  };

  const updateFlexType = (eventId: string, flexOption: string) => {
    const eventToUpdate = { ...flexMeetingEventMap[eventId] };
    eventToUpdate.flexType = {
      type: flexOption === "Move within the day" ? "Day" : "Week",
      dayOfWeek: [],
    };
    setFlexMeetingEventMap({ ...flexMeetingEventMap, [eventId]: eventToUpdate });
  };

  const onSaveAndMoveForward = () => {
    const eventsToEnable = flexMeetingList.reduce((idList: string[], current: FlexMeeting) => {
      if (current.flexible) {
        idList.push(current.id);
      }
      return idList;
    }, []);

    // only explicitly disable events that are marked as flexible via eventMap
    const eventsToDisable = canSetDisabled
      ? flexMeetingList.reduce((idList: string[], current: FlexMeeting) => {
          if (!current.flexible && eventMap[current.id].flexible === true) {
            idList.push(current.id);
          }
          return idList;
        }, [])
      : [];

    if (eventsToEnable?.length > 0 || eventsToDisable?.length > 0) {
      track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_ENABLED, {
        enabledCount: eventsToEnable.length,
        funnelType: funnelType,
      });
      setDisableButtons(true);
      void update({
        variables: { events: flexMeetingList, eventIdsToToDisable: eventsToDisable },
        onCompleted: () => {
          onClickPrimaryButton();
          setDisableButtons(false);
        },
        onError: (error) => {
          setDisableButtons(false);
          setError({
            message: "failed to make meetings flexible",
            error: error,
            showUserMessage: true,
          });
          onClickPrimaryButton();
        },
      });
    } else {
      track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_ENABLED, {
        enabledCount: eventsToEnable?.length,
        funnelType: funnelType,
      });
      onClickPrimaryButton();
    }
  };

  const onSkip = () => {
    track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_SKIPPED, { funnelType: funnelType });
    onClickPrimaryButton();
  };

  return (
    <SlideLayout>
      <Header>The key? A flexible calendar.</Header>
      <Text>
        Marking events as flexible gives Clockwise the power to resolve conflicts automatically and
        maximize your focus time.
      </Text>
      <div className=" cw-w-full md:cw-w-[600px] cw-text-sm cw-text-muted">
        <div className="cw-flex cw-gap-5 cw-text-center cw-mb-12 cw-text-11">
          <div className="cw-flex-1">
            <div>
              <Alarm size={16} weight="fill" color="black" />
            </div>
            We'll adjust your flexible meetings once a day
          </div>
          <div className="cw-flex-1">
            <div>
              <Prohibit size={16} color="black" weight="bold" />
            </div>
            Clockwise will not move events occurring in the next 24 hours
          </div>
          <div className="cw-flex-1">
            <div>
              <Envelope size={16} color="black" />
            </div>
            We'll notify all attendees when we move an event
          </div>
        </div>
        <div className="cw-flex cw-font-semibold cw-items-center cw-h-6 cw-pb-1 cw-border-solid cw-border-b cw-border-muted cw-text-12">
          <div className="cw-flex cw-flex-1">Meeting</div>
          <div className="cw-flex cw-w-[77px]">Repeats</div>
          <div className="cw-flex cw-justify-start cw-w-[182px]">Flexible</div>
        </div>
        {sortedMeetingsByRecurrence.map((meeting) => {
          return (
            <div key={meeting.id} className="cw-flex cw-items-center cw-min-h-[24px] cw-my-1">
              <div
                className={classNames("cw-flex cw-flex-1 cw-font-medium", {
                  "cw-text-positive-pressed": meeting.flexible,
                  "cw-text-default-disabled": !meeting.flexible,
                })}
              >
                {meeting.flexible ? `❇️ ${meeting.title}` : meeting.title}
              </div>
              <div className="cw-flex cw-w-[75px]">
                {meeting.recurrence === RecurrenceType.Fortnightly
                  ? "Biweekly"
                  : meeting.recurrence === RecurrenceType.LessThanMonthly
                  ? "Other"
                  : meeting.recurrence || "Other"}
              </div>
              <div className="cw-flex cw-text-end  cw-gap-1 cw-justify-end">
                <Switch
                  size="small"
                  checked={Boolean(meeting.flexible)}
                  onChange={(toggle: boolean) => {
                    updateFlexibility(meeting.id, toggle);
                  }}
                />
                <div className="cw-w-[151px]">
                  <Select
                    fullWidth
                    showExpandIcon={true}
                    disabled={!meeting.flexible}
                    onChange={(value: string) => {
                      updateFlexType(meeting.id, value);
                    }}
                    variant="inline"
                    value={
                      meeting.flexType?.type === "Week"
                        ? "Move within the week"
                        : "Move within the day"
                    }
                    size="mini"
                  >
                    <SelectOption value={"Move within the day"}>Move within the day</SelectOption>
                    <SelectOption value={"Move within the week"}>Move within the week</SelectOption>
                  </Select>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <ButtonGroup
        shouldShowSecondary={showSecondaryButton}
        primaryLabel={"Confirm"}
        onClickPrimary={onSaveAndMoveForward}
        onClickSecondary={onClickSecondaryButton}
        onSkip={onSkip}
        disabled={disableButtons}
        powerMeeterStrength={getStrengthOfMeeter(flexMeetingList)}
      />
    </SlideLayout>
  );
};

const recurrenceSorted = [
  RecurrenceType.Daily,
  RecurrenceType.Weekly,
  RecurrenceType.Fortnightly,
  RecurrenceType.Monthly,
  RecurrenceType.LessThanMonthly, // This is shown as "other", we place it at the end
];

const sortFlexMeetings = (flexMeetings: FlexMeeting[]) => {
  return flexMeetings.sort(
    (a, b) =>
      recurrenceSorted.indexOf(a.recurrence || RecurrenceType.Monthly) -
      recurrenceSorted.indexOf(b.recurrence || RecurrenceType.Monthly),
  );
};

const getStrengthOfMeeter = (flexMeetings: FlexMeeting[]) => {
  if (!flexMeetings.length) return 4;
  const totalMeetings = flexMeetings.length;
  const flexibleMeetings = flexMeetings.filter((meeting) => meeting.flexible).length;
  const percentage = flexibleMeetings / totalMeetings;
  if (percentage < 0.25) return 1;
  if (percentage < 0.5) return 2;
  if (percentage < 0.75) return 3;
  return 4;
};
